/**
 * LENDER PORTAL (Phase 4, second half). Deploy capital against tokenized collateral: review
 * applications, approve loans, monitor the portfolio. Preview: lending activates in a later sandbox
 * phase, so decisions here are simulated and move no money.
 */

import { useState } from 'react';
import { fmtDateShort } from '@/lib/format';
import { api } from '@/lib/api';
import { StatTile as Stat } from '@/components/dash';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';
import { Reveal, Stagger, StaggerItem } from '@/components/motion';

export default function Lender() {
  const { data, loading, error, reload } = useResource(() => api.lender(), []);
  const [decided, setDecided] = useState<Record<string, 'approved' | 'declined'>>({});

  function decide(id: string, decision: 'approved' | 'declined') {
    // Preview: record the decision in the UI. The real path posts to the decision endpoint under
    // maker-checker when lending activates.
    setDecided((d) => ({ ...d, [id]: decision }));
  }

  if (loading)
    return (
      <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
        <div className="space-y-6 lg:col-span-2">
          <LoadingCard lines={2} />
          <LoadingCard lines={4} />
        </div>
        <div className="space-y-4">
          <LoadingCard lines={3} />
        </div>
      </div>
    );
  if (error) return <ErrorState body="We could not load the lender portal." onRetry={reload} />;
  if (!data) return null;

  const p = data.portfolio;
  const pending = data.applications.filter((a) => !decided[a.id]);

  return (
    <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
      <div className="space-y-6 lg:col-span-2">
        <header>
          <h1 className="text-page-title text-ink">Lending</h1>
          <p className="mt-0.5 text-sm text-ink-muted">
            Deploy capital against tokenized collateral.
          </p>
        </header>

        <div className="rounded-card border border-info/30 bg-info/5 p-3 text-sm text-ink-muted">
          <span className="font-medium text-info">Preview.</span> Lending activates in a later
          sandbox phase. Decisions here are simulated.
        </div>

        {/* PORTFOLIO HERO */}
        <Reveal>
          <section className="relative overflow-hidden rounded-xl bg-gradient-to-br from-primary to-primary-dark p-5 text-white shadow-lifted">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-10 -top-12 h-44 w-44 rounded-full bg-primary-light/40 blur-2xl"
            />
            <div className="relative">
              <p className="text-sm text-white/70">Capital outstanding</p>
              <p className="mt-1 flex items-baseline gap-2">
                <span className="text-sm font-medium text-white/70">KES</span>
                <span className="tabular text-4xl font-semibold tracking-tight">
                  {p.outstanding}
                </span>
              </p>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-white/75">
                <span>KES {p.deployed} deployed</span>
                <span>KES {p.earned} earned</span>
              </div>
            </div>
          </section>
        </Reveal>

        <div className="grid grid-cols-3 gap-3">
          <Stat label="Active loans" value={String(p.activeLoans)} />
          <Stat label="Default rate" value={`${p.defaultRate}%`} accent />
          <Stat label="Products" value={String(data.products.length)} />
        </div>

        {/* APPLICATIONS TO REVIEW */}
        <section>
          <h2 className="mb-2 font-semibold text-ink">Applications</h2>
          {pending.length === 0 ? (
            <p className="rounded-card border border-border bg-surface p-6 text-center text-sm text-ink-muted shadow-card">
              No applications waiting.
            </p>
          ) : (
            <Stagger className="space-y-3">
              {pending.map((a) => (
                <StaggerItem key={a.id}>
                  <div className="rounded-card border border-border bg-surface p-4 shadow-card">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-ink">{a.borrower}</p>
                        <p className="text-xs text-ink-subtle">
                          {a.collateral} &middot; {a.ltvPct}% LTV
                        </p>
                      </div>
                      <span className="rounded-pill bg-surface-sunken px-2 py-0.5 text-micro font-medium text-ink-muted">
                        Risk {a.riskGrade}
                      </span>
                    </div>
                    <p className="tabular mt-2 text-lg font-semibold text-ink">KES {a.amount}</p>
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => decide(a.id, 'declined')}
                        className="flex-1 rounded-field border border-border py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
                      >
                        Decline
                      </button>
                      <button
                        onClick={() => decide(a.id, 'approved')}
                        className="flex-1 rounded-field bg-primary py-2 text-sm font-medium text-white transition hover:bg-primary-light"
                      >
                        Approve
                      </button>
                    </div>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </section>

        {/* PORTFOLIO / ACTIVE LOANS */}
        <section>
          <h2 className="mb-2 font-semibold text-ink">Portfolio</h2>
          <ul className="divide-y divide-border overflow-hidden rounded-card border border-border bg-surface shadow-card">
            {data.loans.map((l) => (
              <li key={l.id} className="flex items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{l.borrower}</p>
                  <p className="text-xs text-ink-subtle">Next: {fmtDateShort(l.nextDue)}</p>
                </div>
                <span className="tabular text-sm font-semibold text-ink">KES {l.outstanding}</span>
                <span
                  className={`w-20 text-right text-xs font-medium capitalize ${l.status === 'performing' ? 'text-accent' : 'text-warning'}`}
                >
                  {l.status}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* RAIL: products */}
      <div className="space-y-4">
        <section className="rounded-card border border-border bg-surface p-5 shadow-card">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-ink">Lending products</h2>
            <button className="text-sm font-medium text-brand hover:underline">+ New</button>
          </div>
          <ul className="mt-3 space-y-2">
            {data.products.map((pr) => (
              <li key={pr.id} className="rounded-field border border-border p-3">
                <p className="text-sm font-medium text-ink">{pr.name}</p>
                <p className="mt-0.5 text-xs text-ink-muted">
                  {pr.ratePct}% p.a. &middot; up to {pr.maxLtvPct}% LTV
                </p>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
